/* Next */
import { GetStaticPaths, GetStaticProps } from 'next'

/* Types */
import { hasOwnProperty } from 'src/functions/typeGuards'
import { ParsedUrlQuery } from 'querystring'
import { tProductMagazineReference, tProductPreview } from 'src/shared/frontendTypes/product'
import { isFilterProductsResponse } from 'src/shared/frontendTypes/productsFilter'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import { LandingPageProductInquirySettings } from 'src/shared/dbTypes/landingPageSettings'
/* Functions */
import { post } from 'src/functions/fetch'
import { saveUrl } from 'src/functions/regex'

/* Options */
import config from 'src/config'
import { fetchSiteShopSettings } from 'src/pageBuilder/sitePageBuilder'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

/* Components */
import Filter from 'src/pages/Filter/index'

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

const buildMode = () =>
  String(process.env.PRODUCT_BUILD_MODE || '').trim().toLowerCase()

const skipQuickSsrPaths = () =>
  process.env.CACHE === 'ssr' && buildMode() === 'quick'

/* Erweiterte Props-Schnittstelle um die SEO-Felder und canonicalUrl */
interface Props {
  bid: number
  title?: string
  description?: string
  products: tProductPreview[]
  count: number
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  canonicalUrl?: string
  brandLogo?: string
  pageBuilder?: MenuPageBuilder
  sitePageBuilder?: MenuPageBuilder
  productInquiry?: LandingPageProductInquirySettings
  magazines?: tProductMagazineReference[]
  landingPageImageAiGenerated?: LandingPageImageAiGenerated
}

const FilterPage: React.FC<Props> = ({ 
  bid, title, description, products, count, metaTitle, metaDescription, metaKeywords, canonicalUrl, brandLogo, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated
}: Props) => 
  <Filter {...{ type: 'brand', bid, title, description, products, count, metaTitle, metaDescription, metaKeywords, canonicalUrl, brandLogo, pageBuilder, sitePageBuilder, magazines, landingPageImageAiGenerated }} />

export default FilterPage

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
/*                              Static Paths & Props                               */
/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */

export const getStaticPaths: GetStaticPaths = async () => {
  const size = (() => {
    if (skipQuickSsrPaths()) return 0

    switch (process.env.CACHE) {
      case 'static': return config.cache_static_brands_size
      case 'ssr':    return config.cache_ssr_brands_size
      default:       return 1
    }
  })()

  if (size <= 0) {
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const data = await post({
    url: 'build/menuUrls/brands', 
    body: { 
      ...config.shop, 
      size,
      menu: process.env.NEXT_PUBLIC_MENU,
      token: process.env.BUILD_TOKEN,
    }, 
    local: true,
  })

  type item = { id: number; path: string }
  const isItems = (arg: unknown): arg is item[] =>
    Array.isArray(arg) &&
    arg.every(obj =>
      typeof obj === 'object' && obj !== null &&
      hasOwnProperty(obj, 'id') && typeof obj.id === 'number' &&
      hasOwnProperty(obj, 'path') && typeof obj.path === 'string'
    )

  if (!isItems(data)) {
    console.error('Kann Marken-Pfade nicht laden!', data)
    return {
      paths: [],
      fallback: process.env.BUILD ? false : 'blocking',
    }
  }

  const paths = data.map(({ id, path }: item) => {
    const bid = id.toString()
    const b_alias = saveUrl(path || bid)
    return {
      params: { bid, b_alias },
    }
  })

  return {
    paths,
    fallback: process.env.BUILD ? false : 'blocking',
  }
}

/* ------------------------------------------------------------------------------- */

interface IParams extends ParsedUrlQuery {
  bid: string
  b_alias: string
}

export const getStaticProps: GetStaticProps<Props> = async ({ params }) => {
  const { bid, b_alias } = params as IParams
  const bidNumber = Number(bid)

  const siteSettings = await fetchSiteShopSettings({ local: true })

  let data: unknown
  try {
    data = await post({
      url: `products`,
      body: {
        ...config.shop,
        brandId: bidNumber,
        filterTree: true,
      },
      local: true
    })
  } catch (err) {
    console.error('Fehler beim Laden der Markenseite', bidNumber, err)
    return { notFound: true }
  }

  if (!isFilterProductsResponse(data)) {
    console.error('Kein valides Response für Markenseite:', bidNumber, data)
    return { notFound: true }
  }

  const canonicalBrandAlias = saveUrl(data.brand?.path || b_alias || String(bidNumber))
  const canonicalUrl = `b/${canonicalBrandAlias}/${bidNumber}`
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    data.landingPageImageAiGenerated,
    siteSettings.landingPageImageAiGenerated,
  )

  if (canonicalBrandAlias && canonicalBrandAlias !== b_alias) {
    return {
      redirect: {
        destination: `/${canonicalUrl}`,
        permanent: true,
      },
    }
  }

  return {
    props: {
      bid: bidNumber,
      title: data.brand?.titel,
      metaTitle: data.brand?.metaTitle,
      metaDescription: data.brand?.metaDescription,
      metaKeywords: data.brand?.metaKeywords,
      description: data.brand?.description,
      ...(data.brand?.logo ? { brandLogo: data.brand.logo } : {}),
      ...(data.brand?.pageBuilder ? { pageBuilder: data.brand.pageBuilder } : {}),
      ...(data.brand?.magazines ? { magazines: data.brand.magazines } : {}),
      count: data?.filterProducts.count || 0,
      products: data?.filterProducts.products || [],
      canonicalUrl,
      ...(siteSettings.sitePageBuilder ? { sitePageBuilder: siteSettings.sitePageBuilder } : {}),
      ...(siteSettings.productInquiry ? { productInquiry: siteSettings.productInquiry } : {}),
      ...(landingPageImageAiGenerated ? { landingPageImageAiGenerated } : {}),
    },
  }
}

/* +++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++++ */
